/* ============================================================================
   GALERI WORKFLOW — data + semakan (tulen, tiada Firebase) · 26 Okt 2026
   ============================================================================
   Page `/workflow` (admin) memapar SEMUA jenis workflow Flow dalam SESAAT sebagai card.
   Arahan user 26 Okt: "button add workflow aku nak … aku boleh edit nama dan lain2,
   tak perlu tunggu melalui kau" → senarai disimpan dalam Firestore (services/galeriWorkflowService.js),
   bukan dalam kod. Fail ini = bentuk data + semakan + senarai permulaan.

   ⚠️ Card di sini = KATALOG (nama · penerangan · status · gambar aliran). Robot sebenar dipilih oleh
      module (scriptKey) + Control Panel. ★ 3 Nov: medan `robot` menyambung card → dropdown Workflow
      Control Panel (Workspace v4 mod New) — peraturan dalam utils/workflowRobot.js.
   ⛔ Prinsip 4: setiap nilai disemak sebelum ditulis (panjang, enum, had senarai) — data dari Firestore
      juga disemak semula semasa dibaca (item rosak dilangkau DAN dikira, bukan senyap).
   ========================================================================== */
import { ID_ROBOT_WF } from './workflowRobot.js';

export const KATEGORI_WF = [
    { id: 'gv', label: 'Image + video' },
    { id: 'v', label: 'Video only' },
    { id: 'g', label: 'Image only' },
    { id: 't', label: 'Tools' },
];

export const STATUS_WF = ['published', 'draft'];

export const HASIL_WF = [
    { id: 'tiktok', label: 'TikTok' },
    { id: 'facebook', label: 'Facebook' },
    { id: 'download', label: 'Download' },
];

/* Gambar aliran pada card — token dilukis dengan ikon oleh page:
   g = gambar AI · c = gambar card · t = teks/prompt · v = video · m = template
   > = anak panah · + = tambah · | = pemisah · s = bintang · e = mata · r = ulang · k = gambar lulus */
export const ILUSTRASI_WF = [
    { id: 'gambar-video', label: 'Image → video', token: 'g>v' },
    { id: 'gambar-2video', label: 'Image → 2 videos', token: 'g>vv' },
    { id: 'gambar-banyak', label: 'Image → many videos', token: 'g>vvvv' },
    { id: 'berlainan', label: 'New image every scene', token: 'gv|gv|gv' },
    { id: 'template', label: 'Template → video', token: 'm>v' },
    { id: 'before-after', label: 'Before / after', token: 'g>k>v' },
    { id: 'kad-video', label: 'Card image → video', token: 'c>v' },
    { id: 'teks-video', label: 'Text → video', token: 't>v' },
    { id: 'versi', label: 'Versions → best', token: 'g>vvv>s' },
    { id: 'extend', label: 'Extend video', token: 'v+v' },
    { id: 'gambar-sahaja', label: 'Text → images', token: 't>ggg' },
    { id: 'semak', label: 'Review image first', token: 'ge>v' },
    { id: 'ulang', label: 'Redo video', token: 'kr>v' },
];

export const HAD_WF = { nama: 60, penerangan: 160, tag: 24, bilTag: 3, senarai: 100, susunan: 999 };

const ID_SAH = /^[a-z0-9_-]{2,40}$/;
const idKategori = new Set(KATEGORI_WF.map((k) => k.id));
const idHasil = new Set(HASIL_WF.map((h) => h.id));
const idIlustrasi = new Set(ILUSTRASI_WF.map((i) => i.id));

export const ilustrasiWf = (id) => ILUSTRASI_WF.find((i) => i.id === id) || ILUSTRASI_WF[0];

/** "1–5 scenes, Pick 1 image" → ['1–5 scenes', 'Pick 1 image'] (kosong dibuang, pendua dibuang). */
export function pecahTag(teks) {
    const asal = Array.isArray(teks) ? teks : String(teks ?? '').split(',');
    const out = [];
    for (const x of asal) {
        const t = String(x ?? '').trim();
        if (t && !out.includes(t)) out.push(t);
    }
    return out;
}

/**
 * Semak + bersihkan satu workflow. Gagal = { ok:false, ralat } dengan ayat jelas (admin UI = English).
 * @returns {{ok:true, wf:object} | {ok:false, ralat:string}}
 */
export function normalWorkflow(input) {
    const x = input && typeof input === 'object' ? input : {};
    const id = String(x.id ?? '').trim();
    if (!ID_SAH.test(id)) return { ok: false, ralat: 'Invalid workflow id.' };

    const nama = String(x.nama ?? '').trim();
    if (!nama) return { ok: false, ralat: 'Name is required.' };
    if (nama.length > HAD_WF.nama) return { ok: false, ralat: `Name is too long (max ${HAD_WF.nama} characters).` };

    const penerangan = String(x.penerangan ?? '').trim();
    if (penerangan.length > HAD_WF.penerangan) return { ok: false, ralat: `Description is too long (max ${HAD_WF.penerangan} characters).` };

    const status = String(x.status ?? '');
    if (!STATUS_WF.includes(status)) return { ok: false, ralat: 'Status must be Published or Draft.' };

    const kategori = String(x.kategori ?? '');
    if (!idKategori.has(kategori)) return { ok: false, ralat: 'Pick a category.' };

    const ilustrasi = String(x.ilustrasi ?? '');
    if (!idIlustrasi.has(ilustrasi)) return { ok: false, ralat: 'Pick a picture for the card.' };

    const tag = pecahTag(x.tag);
    if (tag.length > HAD_WF.bilTag) return { ok: false, ralat: `Max ${HAD_WF.bilTag} tags.` };
    const tagPanjang = tag.find((t) => t.length > HAD_WF.tag);
    if (tagPanjang) return { ok: false, ralat: `Tag "${tagPanjang.slice(0, 30)}…" is too long (max ${HAD_WF.tag} characters).` };

    const hasilMentah = Array.isArray(x.hasil) ? x.hasil : [];
    const hasil = HASIL_WF.map((h) => h.id).filter((h) => hasilMentah.includes(h));
    if (hasilMentah.some((h) => !idHasil.has(h))) return { ok: false, ralat: 'Unknown output destination.' };

    const susunanNum = Number(x.susunan);
    if (!Number.isFinite(susunanNum) || susunanNum < 0 || susunanNum > HAD_WF.susunan) {
        return { ok: false, ralat: `Order must be a number from 0 to ${HAD_WF.susunan}.` };
    }
    const susunan = Math.round(susunanNum);

    const dikemaskini = Number(x.dikemaskini) > 0 ? Math.round(Number(x.dikemaskini)) : 0;
    const wf = { id, nama, penerangan, status, kategori, ilustrasi, tag, hasil, susunan, dikemaskini };

    /* ★ [3 Nov] `robot` = sambungan card → robot (utils/workflowRobot.js). Medan TIADA = ikut lalai id
       (ROBOT_LALAI); '' = admin pilih "Not linked". Hanya ditulis bila ada, supaya doc lama tidak berubah bentuk. */
    if (x.robot !== undefined && x.robot !== null) {
        const robot = String(x.robot);
        if (robot && !ID_ROBOT_WF.has(robot)) return { ok: false, ralat: 'Unknown robot.' };
        wf.robot = robot;
    }
    return { ok: true, wf };
}

/** Baca senarai mentah dari Firestore → { senarai sah, rosak: bilangan item dilangkau }. */
export function bacaSenaraiWorkflow(mentah) {
    const senarai = [];
    let rosak = 0;
    const dilihat = new Set();
    for (const x of Array.isArray(mentah) ? mentah : []) {
        const n = normalWorkflow(x);
        if (!n.ok || dilihat.has(n.wf.id)) { rosak++; continue; }
        dilihat.add(n.wf.id);
        senarai.push(n.wf);
    }
    return { senarai: susunWorkflow(senarai), rosak };
}

export function susunWorkflow(senarai) {
    return [...senarai].sort((a, b) => (a.susunan - b.susunan) || a.nama.localeCompare(b.nama));
}

/** tab: 'all' | 'published' | 'draft' · cari: teks bebas (nama, penerangan, tag). */
export function tapisWorkflow(senarai, { tab = 'all', cari = '' } = {}) {
    const q = String(cari).trim().toLowerCase();
    return senarai.filter((w) => {
        if (tab !== 'all' && w.status !== tab) return false;
        if (!q) return true;
        return [w.nama, w.penerangan, ...w.tag].some((s) => s.toLowerCase().includes(q));
    });
}

export function idWorkflowBaru() {
    return `wf_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/** Workflow kosong untuk butang "Create workflow" — susunan selepas yang terakhir. */
export function workflowKosong(senarai = []) {
    const akhir = senarai.reduce((m, w) => Math.max(m, w.susunan), 0);
    return {
        id: idWorkflowBaru(), nama: '', penerangan: '', status: 'draft', kategori: 'gv',
        ilustrasi: 'gambar-video', tag: [], hasil: ['tiktok', 'facebook', 'download'],
        susunan: Math.min(HAD_WF.susunan, akhir + 10), dikemaskini: 0,
    };
}

/* ── Senarai permulaan (dimuat SEKALI atas klik admin "Load starter workflows") ──
   Status ikut keadaan 26 Okt 2026: published = pelanggan guna hari ini (robot lama);
   draft = belum siap (enjin Flow Resipi + workflow yang dirancang). Teks English (pilot) —
   admin boleh ubah semuanya selepas dimuat. */
const SEMUA = ['tiktok', 'facebook', 'download'];
export const WORKFLOW_MULA = [
    { id: 'wf-1scene', nama: 'AI image to video, 1 scene', penerangan: 'One product image, one 8-second video.', status: 'published', kategori: 'gv', ilustrasi: 'gambar-video', tag: ['1 scene', 'Up to 3 sets'], hasil: SEMUA, susunan: 10 },
    { id: 'wf-2scene', nama: 'AI image to video, 2 scenes', penerangan: 'Same image, two scenes combined into one video.', status: 'published', kategori: 'gv', ilustrasi: 'gambar-2video', tag: ['2 scenes', '16 seconds'], hasil: SEMUA, susunan: 20 },
    { id: 'wf-berlainan', nama: 'New image every scene', penerangan: 'Each scene gets its own image, combined into one story.', status: 'published', kategori: 'gv', ilustrasi: 'berlainan', tag: ['2–5 scenes'], hasil: SEMUA, susunan: 30 },
    { id: 'wf-template', nama: 'Motion template', penerangan: 'Ready-made movement styles for the product.', status: 'published', kategori: 'gv', ilustrasi: 'template', tag: ['1 scene'], hasil: SEMUA, susunan: 40 },
    { id: 'wf-kad', nama: 'Video from card images', penerangan: 'Use your own product photos, no AI image.', status: 'published', kategori: 'v', ilustrasi: 'kad-video', tag: ['No AI image'], hasil: SEMUA, susunan: 50 },
    { id: 'wf-sama', nama: 'One image, many scenes', penerangan: 'Same product in every scene. Combine or download each scene.', status: 'draft', kategori: 'gv', ilustrasi: 'gambar-banyak', tag: ['1–5 scenes'], hasil: SEMUA, susunan: 60 },
    { id: 'wf-before-after', nama: 'Before and after', penerangan: 'Start and end images, video in between.', status: 'draft', kategori: 'gv', ilustrasi: 'before-after', tag: ['1 scene'], hasil: SEMUA, susunan: 70 },
    { id: 'wf-teks', nama: 'Text to video', penerangan: 'Video straight from the prompt, no image.', status: 'draft', kategori: 'v', ilustrasi: 'teks-video', tag: ['1–5 scenes'], hasil: SEMUA, susunan: 80 },
    { id: 'wf-versi', nama: 'Several versions, pick the best', penerangan: 'Same prompt, a few videos to choose from.', status: 'draft', kategori: 'v', ilustrasi: 'versi', tag: ['2–4 versions'], hasil: SEMUA, susunan: 90 },
    { id: 'wf-extend', nama: 'Extend video', penerangan: 'Make an existing video longer.', status: 'draft', kategori: 'v', ilustrasi: 'extend', tag: ['+8 seconds each'], hasil: SEMUA, susunan: 100 },
    { id: 'wf-gambar', nama: 'Images only', penerangan: 'Generate product images, no video.', status: 'draft', kategori: 'g', ilustrasi: 'gambar-sahaja', tag: ['1–4 images'], hasil: ['download'], susunan: 110 },
    { id: 'wf-semak', nama: 'Review images first', penerangan: 'Approve the image, then the video is made. Saves credits.', status: 'draft', kategori: 't', ilustrasi: 'semak', tag: ['Saves credits'], hasil: SEMUA, susunan: 120 },
    { id: 'wf-ulang', nama: 'Redo video only', penerangan: 'Keep the image, generate the video again.', status: 'draft', kategori: 't', ilustrasi: 'ulang', tag: ['Saves credits'], hasil: SEMUA, susunan: 130 },
];
